import { VideoJsAdapter } from '../adapters';
import { __PLANET_MEDIA_PLAYER_BASE_URL__ } from '../constants';
import { PluginConfiguration } from '../plugin';
import { EventEmitterBase, ScriptLoader } from '../utils';
import { BaseMediaController } from './base-media-controller';
import { PlanetMediaPlayerImplementation } from './enums';
import { InstanceManager } from './instance-manager';
import { ImplementationDictionary, PlanetMediaPlayerOptions, PlayerImplementationDescriptor } from './models';

export class PlayerBuilder extends EventEmitterBase {

    private readonly base: string = __PLANET_MEDIA_PLAYER_BASE_URL__;
    protected loader: ScriptLoader = new ScriptLoader();

    protected implementations: ImplementationDictionary = {
        videojs: {
            name: 'videojs',
            dependencies: [],
            factory: (containerId: string, options?: PluginConfiguration[]) => this.createVideoJs(containerId, options),
            exists: () => typeof window['videojs'] === 'function',
        },
    };

    constructor() {
        super();
    }

    /**
     * Creates the DOM necessary to create the player
     * @param implementation
     * @param container HTMLElement
     * @param id string
     * @param isAudio
     */
    createPlayerElement(
        implementation: PlayerImplementationDescriptor,
        container: HTMLElement,
        id: string,
        isAudio: boolean = false,
    ): HTMLElement {
        let playerElement: HTMLElement;
        switch (implementation.name) {
            case PlanetMediaPlayerImplementation.VideoJs:
                playerElement = this.createVideoJsElement(id, [
                    'video-js',
                    'vjs-planet',
                ], isAudio);
        }
        container.appendChild(playerElement);
        return playerElement;
    }

    /**
     * Returns the HTMLElement that will hold DOMElements for the player.
     * @param container
     */
    getContainer(container: string | HTMLElement): HTMLElement {
        if (container instanceof HTMLElement) {
            if (container.nodeName.toLowerCase() === 'div') {
                return container;
            }
        }
        if (typeof container === 'string') {
            return document.getElementById(container);
        }
        throw new Error('Invalid container. The `containerOrId` parameter must be a div HTMLElement it\'s id');
    }

    /**
     * Create an instance of IMediaController
     * @param containerOrId string | HTMLElement
     * @param options [PlanetMediaPlayerOptions]
     */
    async createPlayer(containerOrId: string | HTMLElement, options?: PlanetMediaPlayerOptions): Promise<BaseMediaController> {
        let implementationBuilder: PlayerImplementationDescriptor;

        implementationBuilder = this.implementations[PlanetMediaPlayerImplementation.VideoJs];

        const implementation: PlayerImplementationDescriptor = await this.fetchDependencies(implementationBuilder);

        const playerContainer: HTMLElement = this.getContainer(containerOrId);
        options?.plugins?.forEach(c => c.factory = () => InstanceManager.getPlugin(c.type || c.name));
        const player: BaseMediaController = await implementation.factory(playerContainer.getAttribute('id'), options.plugins);
        const playerId: string = player.getId();

        this.emit('player:created', {
            playerId,
            player,
        });

        if (options && typeof options.onApiReady === 'function') {
            options.onApiReady.call(player);
        }

        return player;
    }

    /**
     * @param implementation
     */
    private async fetchDependencies(implementation: PlayerImplementationDescriptor): Promise<PlayerImplementationDescriptor> {
        if (implementation) {
            try {
                await this.loader.require(implementation.dependencies.map(p => {
                    const base: string = this.base;
                    const path: string = p.replace(/^[\/]+|[\/]+$/g, '');
                    return `${base}/${path}`;
                }));
                return implementation;
            } catch (e) {
                this.emit('setupError', {
                    name: 900600,
                    message: 'Cannot load player dependencies. Make sure the parameter `base` is present and contains the proper url',
                    sourceError: e,
                });
                return null;
            }
        }
    }

    private createVideoJs(containerId: string, options?: PluginConfiguration[]): VideoJsAdapter {
        return new VideoJsAdapter(containerId, options);
    }

    /**
     * Creates the video tag for videojs
     * @param id string
     * @param classList string[]
     * @param isAudio boolean
     */
    private createVideoJsElement(id: string, classList: string[] = [], isAudio: boolean = false): HTMLVideoElement | HTMLAudioElement {
        const type: 'audio' | 'video' = isAudio ? 'audio' : 'video';
        const mediaElement: HTMLVideoElement | HTMLAudioElement = document.createElement(type);
        mediaElement.setAttribute('id', id);
        mediaElement.setAttribute('class', classList.join(' '));
        mediaElement.setAttribute('playsinline', '');
        return mediaElement;
    }
}

